import { ExecutionResult } from 'graphql';
import { ObjMap } from 'graphql/jsutils/ObjMap';
import { useCallback, useEffect, useState } from 'react';
import { TestingClient, State, GetAddressQuery } from '../GraphQL.Testing';
import { IncomeCalculationCycle } from '../loan-application/income/IncomeCalculationCycle';
import {
    setCheckbox,
    setRadioButton,
    setSelectValue,
    setTextBox,
    isHtmlFormElement,
    isHtmlInputElement,
    isHtmlSelectElement,
} from './DOMHelpers';
import { useLocalization } from './localization';
import { Spinner } from './Spinner';
import { isNullOrEmpty } from './StringHelper';
import { useGraphQL } from './useGraphQL';


type MockValue = string | number | (() => Promise<string | number | { model: string | number }>);
const clicked = new Set<string>();
let form: HTMLFormElement | null = null;

async function mock(query: string, value: MockValue) {
    const elements = Array.from(document.querySelectorAll(query));

    let handled = false;
    for (const element of elements) {
        if (element.id && element.classList.contains('js-click')) {
            let uuid = window.location.pathname + '#' + element.id;
            if (!clicked.has(uuid)) {
                clicked.add(uuid);
                element.dispatchEvent(
                    new MouseEvent('click', {
                        view: window,
                        bubbles: true,
                        cancelable: true,
                    })
                );
            }
            handled = true;
        } else if (isHtmlFormElement(element)) {
            if (typeof value === 'function') {
                const result = await value();
                value = typeof result === 'number' || typeof result === 'string' ? result : result.model;
            }

            if (isHtmlSelectElement(element)) {
                form = element.form;
                setSelectValue(element, value);
                handled = true;
            } else if (isHtmlInputElement(element)) {
                form = element.form;
                switch (element.type.toLowerCase()) {
                    case 'text':
                    case 'tel':
                    case 'password':
                        setTextBox(element, value);
                        handled = true;
                        break;
                    case 'radio':
                        setRadioButton(element);
                        break;
                    case 'checkbox':
                        setCheckbox(element);
                        break;
                    default:
                        throw new Error(`Unhandled input type ${element.type}`);
                }
            }
        }
    }

    return handled;
}

export function MockData() {
    const [busy, setBusy] = useState(false);

    const locale = useLocalization();
    const graphQLClient = useGraphQL(TestingClient);

    const doDocumentMockData = useCallback(async () => {

        let autoContinueFailsafe = 0;
        while (document.querySelector('.btn-next-panel') && !document.querySelector('#fullNameMatch') && autoContinueFailsafe <= 100) { //Only attempt for ~5 seconds, abandon effort otherwise
            autoContinueFailsafe++;
            if (document.querySelector('.js-checked')) {
                mock('.js-checked', 1);
                await Promise.delay(50);
            }
            if (document.querySelector('.phone-input')) {
                mock('.phone-input', 1);
                await Promise.delay(50);
            }
            (document.querySelector('.btn-next-panel') as HTMLButtonElement).click();
            await Promise.delay(50); // wait for react renders
        }
        const name = document.querySelector('.js-full-name')?.innerHTML ?? "";
        mock('#fullNameMatch', name);
    }, []);


    const doIncomeMockData = useCallback(async () => {
        setBusy(true);

        const getTomorrowFormatted = () => {
            const today = new Date();
            const tomorrow = new Date(today);
            tomorrow.setDate(today.getDate() + 1);
            return tomorrow.toLocaleDateString('en-us');
        }

        const currentIncomeTestType = (document.querySelector('.js-income-test') as HTMLSelectElement)?.value ?? 1; //mid

        const getCurrentGrossIncome = () => {
            switch (currentIncomeTestType) {
                case "0": //low
                    return 200;
                case "1": //mid
                default:
                    return 3000;
                case "2": //high
                    return 20000;
            }
        }
        const getCurrentNetIncome = () => {
            switch (currentIncomeTestType) {
                case "0": //low
                    return 200;
                case "1": //mid
                default:
                    return 3000;
                case "2": //high
                    return 20000;
            }
        }

        const currentIncomeType = (document.querySelector('.js-income-type-code') as HTMLSelectElement)?.value ?? '2';
        if (currentIncomeType === '3') {
            setBusy(false);
            return;
        }
        mock('.js-income-type-code', currentIncomeType === undefined || currentIncomeType === '0' ? '2' : currentIncomeType);
        const setIncomeType = (document.querySelector('.js-income-type-code') as HTMLSelectElement)?.value;

        if (setIncomeType === '1' || setIncomeType === '2') {
            await Promise.delay(200);
            mock('.js-income-source', '1')
        }
        if (currentIncomeType === '1') {
            await Promise.delay(50);
            mock('.js-income-role', '1');
            await Promise.delay(1500);
            mock('.js-income-employer', 'Adventureworks Ltd.');
            await Promise.delay(50);
            mock('.js-income-employer-phone', '(800)-555-5555');
            await Promise.delay(50);
            mock('.js-income-month', '3');
            await Promise.delay(50);
            mock('.js-income-year', '3');
        }
        await Promise.delay(50);
        mock('.js-pay-cycle', 3)
        await Promise.delay(50);
        mock('.js-monthly-pay-date', getTomorrowFormatted());
        await Promise.delay(2000);
        mock('.js-direct-deposit .js-checked', 1);
        await Promise.delay(50);
        mock('.js-income-frequency-monthly', IncomeCalculationCycle.Monthly);
        await Promise.delay(50);
        mock('.js-gross-income', getCurrentGrossIncome());
        await Promise.delay(50);
        mock('.js-net-income', getCurrentNetIncome());

        setBusy(false);
        const submit = form?.querySelector('[type="submit"]') as HTMLButtonElement;
        submit?.focus?.();
    }, [])

    const doDefaultMockData = useCallback(async () => {
        setBusy(true);

        const getRandomNumber = (minValue: number, maxValue: number) => {
            return graphQLClient.getRandomNumber(minValue, maxValue).then((x) => x?.data?.getRandomNumber?.randomNumber ?? 0);
        };

        const stateElement = (document.querySelector('.js-state') as HTMLSelectElement);
        const currentState = (document.querySelector('.js-state') as HTMLSelectElement)?.selectedOptions?.[0].text?.replace(/\W+/g, '');
        const currentStatePromise =
            stateElement === null
                ? new Promise<ExecutionResult<GetAddressQuery, ObjMap<any>>>(() => { return null; })
                : graphQLClient.getAddress(State[(currentState ?? State.Unknown) as keyof typeof State]);
        const states = locale.nonRestrictedStateListItems;

        const currentLastName = (document.querySelector('.js-last-name') as HTMLInputElement)?.value
        const getLastName = () => {
            return graphQLClient.getLastName().then(x => {
                if (!isNullOrEmpty(currentLastName))
                    return currentLastName;
                else
                    return x?.data?.getLastName?.lastName ?? ""
            })
        }
        const currentIDType = (document.querySelector('.js-id-type') as HTMLSelectElement)?.value ?? 1
        const currentIDState = (document.querySelector('.js-id-state') as HTMLSelectElement)?.selectedOptions?.[0].text;
        const getCurrentStateID = () => {
            switch (currentIDType) {
                case '1': //Driver's License
                case '4': //State ID
                default:
                    return (currentIDState === undefined || currentIDState === ''
                        ? graphQLClient.getIDNumber(State.Unknown).then((x) => x?.data?.getIDNumber?.stateIDNumber ?? getRandomNumber(100000, 999999).toString())
                        : graphQLClient.getIDNumber(State[currentIDState?.replace(/\W+/g, '') as keyof typeof State]).then((x) => x?.data?.getIDNumber?.stateIDNumber ?? getRandomNumber(100000, 999999).toString()));
                case '2': //Military ID
                    return getRandomNumber(100000000, 999999999).then((x) => '1' + (x?.toString() ?? "123456789")); //10 digits
                case '3': //Passport
                    return getRandomNumber(10000000, 99999999).then((x) => 'A' + (x?.toString() ?? "12345678")); //US "A" + 8 digits
            }
        }
        
        const getPhoneNumber = async () => {
            const useProveOverride = document.querySelector('.phone-num-test');
            const useProve = useProveOverride?.querySelector('.custom-control-input:checked');
            if (!useProveOverride || useProve) //Only use new behavior on pages with the toggle in the harness
                return "2092144510"
            else {
                let first = await getRandomNumber(2, 7).then((x) => x.toString());      //Exclude 0xx, 1xx, 8xx, and 9xx area codes
                let second = await getRandomNumber(12, 99).then((x) => x.toString());   //Exclude x11 area codes
                while ((new Set(first + second)).size === 1) {                          //Prevent area code from being all same number
                    first = await getRandomNumber(2, 7).then((x) => x.toString());
                    second = await getRandomNumber(12, 99).then((x) => x.toString());
                }
                // (xxx) 555-01xx number is disallowed, but cannot occur due to last 4 digits being unable to start with 0
                const third = await getRandomNumber(100, 999).then((x) => x.toString());
                const fourth = await getRandomNumber(1000, 9999).then((x) => x.toString());
                return first + second + third + fourth;
            }
        }

        const getMaskedPhoneNumber = async () => {
            const useProveOverride = document.querySelector('.phone-num-test');
            const useProve = useProveOverride?.querySelector('.custom-control-input:checked');
            if (!useProveOverride || useProve) //Only use new behavior on pages with the toggle in the harness
                return "(209) 214-4510"
            else {
                let first = await getRandomNumber(2, 7).then((x) => x.toString());      //Exclude 0xx, 1xx, 8xx, and 9xx area codes
                let second = await getRandomNumber(12, 99).then((x) => x.toString());   //Exclude x11 area codes
                while ((new Set(first + second)).size === 1) {                          //Prevent area code from being all same number
                    first = await getRandomNumber(2, 7).then((x) => x.toString());
                    second = await getRandomNumber(12, 99).then((x) => x.toString());
                }
                // (xxx) 555-01xx number is disallowed, but cannot occur due to last 4 digits being unable to start with 0
                const third = await getRandomNumber(100, 999).then((x) => x.toString());
                const fourth = await getRandomNumber(1000, 9999).then((x) => x.toString());
                return "(" + first + second + ') ' + third + '-' + fourth;
            }
        }

        const getBankAccountNumber = async () => {
            const numberOverride = (document.querySelector('.js-bank-verification-vendor') as HTMLSelectElement)?.value;
            if (numberOverride)
                return "754637";
            else
                return await getRandomNumber(100000, 999999);
        }

        const getBankTransitNumber = async () => {
            const numberOverride = (document.querySelector('.js-bank-verification-vendor') as HTMLSelectElement)?.value;
            if (numberOverride)
                return numberOverride;
            else
                return await graphQLClient.getBankTransitNumber().then((x) => x?.data?.getBankTransitNumber?.bankTransitNumber ?? '');
        }

        let queue: [string, MockValue][] = [
            ['.js-click', ''],
            ['.js-mock-0', 0],
            ['.js-mock-1', 1],
            ['.js-mock-2', 2],
            ['.js-mock-3', 3],
            ['.js-mock-4', 4],
            ['.js-mock-5', 5],
            ['.js-mock-1000', 1000],
            ['.js-random-month', () => getRandomNumber(1, 12)],
            ['.js-random-year', () => getRandomNumber(1, 30)],
            ['.js-random-number-small', () => getRandomNumber(10, 1000)],
            ['.js-random-number', () => getRandomNumber(100000, 999999)],
            ['.js-ytd-income', 8500],
            ['.js-federal-tax', 200],
            ['.js-state-tax', 50],
            ['.js-fica', 84],
            ['.js-medicare', 29],
            ['.js-pay-type', 'salary'],
            ['.js-weekly-payday', 6],
            ['.js-marital-status', 'Married'],
            ['.js-bank-name', 'Bank of America'],
            ['.js-bank-transit-number', () => getBankTransitNumber()],
            [
                '.js-bank-institution-number',
                () => graphQLClient.getBankInstitutionNumber().then((x) => x?.data?.getBankInstitutionNumber?.bankInstitutionNumber ?? ''),
            ],
            ['.js-bank-account-number', () => getBankAccountNumber()],
            ['.js-first-name', () => graphQLClient.getFirstName().then((x) => x?.data?.getFirstName?.firstName ?? '')],
            ['.js-last-name', () => getLastName()],
            ['input.js-full-name', document.querySelector('.js-full-name:not(input)')?.textContent?.trim() ?? 'john doe'],
            ['.js-password', () => graphQLClient.getPassword().then((x) => x?.data?.getPassword?.password ?? '')],
            ['.js-email', () => graphQLClient.getEmail().then((x) => x?.data?.getEmail?.email ?? '')],
            [
                '.js-email-verification-code',
                () => graphQLClient.getEmailVerificationCode().then((x) => x?.data?.getEmailVerificationCode?.emailVerificationCode ?? ''),
            ],
            ['.js-visitor-email', () => graphQLClient.getVisitorEmail().then((x) => x?.data?.getVisitorEmail?.visitorEmail ?? '')],
            ['.js-security-question', 2],
            ['.js-security-question-answer', 'Rudy'],
            ['.js-checked input, input.js-checked, .required input[type=checkbox]', 1],
            ['.js-month', 5],
            ['.js-day', 15],
            ['.js-birth-year', 51], // 1952
            ['.js-phone', () => getPhoneNumber()],
            ['.js-phone-masked', () => getMaskedPhoneNumber()],
            [
                '.js-social-security-number',
                () => graphQLClient.getSocialSecurityNumber().then((x) => x?.data?.getSocialSecurityNumber?.socialSecurityNumber ?? ''),
            ],
            ['.js-id-type', currentIDType === undefined || currentIDType === '0' ? '1' : currentIDType],
            ['.js-id-state', currentIDState === undefined || currentIDState === '' ? State.Nevada : currentIDState],
            ['.js-id-number', () => getCurrentStateID()],
            ['.js-id-country', "1"], //United States passport
            //Expiry May 15, 2 years from current
            ['.js-id-month', "5"], 
            ['.js-id-day', "15"], 
            ['.js-id-year', "3"], 

            [
                '.js-vin',
                () =>
                    graphQLClient
                        .getVehicleIdentificationNumber()
                        .then((x) => x?.data?.getVehicleIdentificationNumber?.vehicleIdentificationNumber ?? ''),
            ],
            ['.js-vehicle-make', 'Jeep'],
            ['.js-vehicle-model', 'Wrangler'],
            ['.js-vehicle-type', 'Passenger'],
            ['.js-vehicle-plate', 'IMA PLATE'],
            ['.js-vehicle-condition', 'EXCELLENT'],
            ['.js-fuel-type', 'Gas'],
            [
                '.js-business-ein',
                () =>
                    graphQLClient
                        .getEmployerIdentificationNumber()
                        .then((x) => x?.data?.getEmployerIdentificationNumber?.employerIdentificationNumber ?? ''),
            ],
            ['.js-business-legal', () => getRandomNumber(1, 8)],
            ['.js-business-name', 'Testing Inc.'],
            ['.js-business-purpose', 'Inventory'],
            ['.js-business-type', () => getRandomNumber(1, 11)],
            [
                '.js-customer-initials',
                () => graphQLClient.getCustomerInitials().then((x) => x?.data?.getCustomerInitials?.customerInitials ?? ''),
            ],
            ['.js-street-address', () => currentStatePromise.then((x) => x?.data?.getAddress?.address?.address ?? '')],
            ['.js-city', () => currentStatePromise.then((x) => x?.data?.getAddress?.address?.city ?? '')],
            ['.js-zip', () => currentStatePromise.then((x) => x?.data?.getAddress?.address?.zip ?? '')],
            [
                '.js-state',
                () => currentStatePromise.then((x) => states.findIndex((item) => item.value === x?.data?.getAddress?.address?.state)) ?? 0
            ],
        ];

        let elementsFound = true;
        while (elementsFound && queue.length > 0) {
            const result = await Promise.all(queue.map(async ([query, value]) => { await Promise.delay(50); return mock(query, value); }));
            elementsFound = result.some((x) => x);
            queue = queue.filter((_, i) => !result[i]);
            await Promise.delay(50); // wait for react renders
        }

        setBusy(false);
        await Promise.delay(50); // wait for react renders

        let autoCloseFailsafe = 0;
        while (document.querySelector('.disclosure-modal-accept-btn') && autoCloseFailsafe <= 100) { //Only attempt for ~5 seconds, abandon effort otherwise
            console.log("Accepting Disclosure Modal(s) via Test Harness, attempt " + autoCloseFailsafe + "/100");
            const agree = document.querySelector('.disclosure-modal-accept-btn') as HTMLButtonElement;
            agree.click();
            await Promise.delay(500); // wait for modals to close / additional modals to popup
            autoCloseFailsafe++;
        }

        const submit = form?.querySelector('[type="submit"]') as HTMLButtonElement;
        submit?.focus?.();
    }, [graphQLClient, locale.nonRestrictedStateListItems]);

    const generateMockData = useCallback(async () => {
        const location = document.location.pathname;
        switch (location) {
            case '/LoanApplication/Income':
            case '/LoanApplication/Express/Income':
                await doIncomeMockData();
                break;
            case '/LoanApplication/Documents':
                await doDocumentMockData();
                break;
            default:
                await doDefaultMockData();
                break;
        }
    }, [doDefaultMockData, doIncomeMockData, doDocumentMockData]);

    const handleCtrlM = useCallback(
        (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'm') {
                generateMockData();
            }
        },
        [generateMockData]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleCtrlM);
        return () => document.removeEventListener('keydown', handleCtrlM);
    }, [handleCtrlM]);

    return (
        <button type="button" className="btn btn-secondary max-h-fit max-w-fit" disabled={busy} onClick={generateMockData} title="CTRL+M">
            {busy && <Spinner inline />}
            Mock Form
        </button>
    );
}
